import React, { useMemo } from "react";
import { GoogleMap, Marker } from "@react-google-maps/api";
import { Loader2, MapPin, Navigation, X } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Button } from "../ui/button";
import { GoogleMapError, useGMaps } from "../../lib/gmaps";
import { useApp } from "../../context/AppContext";
import { useProjectLocations } from "../../hooks/useProjectLocations";
import { navigationDestination } from "../../lib/projectLocation";

const CONTAINER = { width: "100%", height: "100%" };

// 项目详情页地址区的 Google 地图链接：有地址优先按地址检索（带地名，最直观），否则退回经纬度。
export const gmapsSearchUrl = (p) => {
  return `https://www.google.com/maps/search/?api=1&query=${navigationDestination(p)}`;
};

// 「查看地图」内置弹窗：按当前门牌重新核对，不能因为旧库有坐标就跳过校验。
// 加载失败 / 解析失败时降级为「在 Google 地图中打开」外链，不阻塞用户。
export default function AddressMapModal({ open, onClose, project, zh }) {
  const { isLoaded, loadError, errorCode } = useGMaps();
  const { api } = useApp();
  const rows = useMemo(() => project ? [project] : [], [project]);
  const [located] = useProjectLocations(rows, api, open && isLoaded);
  const center = located?.lat != null ? { lat: located.lat, lng: located.lng } : null;
  const geoFail = located?.locationStatus === "unresolved";
  const url = gmapsSearchUrl(located || project);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl p-0 gap-0 overflow-hidden" data-testid="hub-address-map-modal">
        <DialogHeader className="p-4 pb-2.5 relative">
          <DialogTitle className="flex items-center gap-2 text-base pr-10">
            <MapPin className="h-4 w-4 text-brand-500 shrink-0" />
            <span className="truncate">{project?.address || (zh ? "项目地址" : "Project address")}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-400">
            {/* 后备插值仅作地图参考；详情卫星图也必须说明精度，不能误认为是建筑屋顶。 */}
            {located?.locationPrecision === "approximate"
              ? (zh ? "约略位置 · 已匹配门牌，图钉为沿街估算位置；请按完整地址导航" : "Approximate · pin estimated along the matched street address; navigate using the full address")
              : (zh ? "内置地图 · 也可点击右下角在 Google 地图中打开" : "Embedded map · or open in Google Maps")}
          </DialogDescription>
          {/* 卡片内关闭按钮：共享 Dialog 的 X 挂在视口右上角（fixed right-4 top-4），
              宽屏下离本弹窗卡片上千像素，用户找不到 —— 这里在卡片内补一个明确的关闭入口。 */}
          <button type="button" onClick={onClose} data-testid="hub-address-map-close"
            title={zh ? "关闭" : "Close"} aria-label={zh ? "关闭" : "Close"}
            className="absolute right-3 top-3 rounded-full p-1.5 text-slate-400 hover:bg-slate-100 hover:text-slate-700 transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500/30">
            <X className="h-4 w-4" />
          </button>
        </DialogHeader>
        <div className="relative h-[46vh] min-h-[300px] max-h-[560px] bg-slate-100" data-testid="hub-address-map-canvas">
          {loadError ? (
            <GoogleMapError zh={zh} errorCode={errorCode} compact testId="hub-address-map-error" />
          ) : geoFail ? (
            <div className="h-full flex flex-col items-center justify-center gap-2 text-slate-400 text-xs p-4 text-center">
              <MapPin className="h-6 w-6" />
              <p>{zh ? "暂未核实到该门牌位置，请核对完整地址或前往 Google 地图查看" : "Street address could not be verified. Check the full address or open Google Maps."}</p>
            </div>
          ) : !isLoaded || !center ? (
            <div className="h-full flex items-center justify-center gap-2 text-slate-400 text-sm">
              <Loader2 className="h-4 w-4 animate-spin" />
              {zh ? "地图加载中…" : "Loading map…"}
            </div>
          ) : (
            <GoogleMap
              mapContainerStyle={CONTAINER}
              center={center}
              zoom={17}
              options={{
                // 地址弹窗只需普通标记，使用默认栅格底图，减少手机对云端矢量样式配置的依赖。
                mapTypeId: "roadmap", clickableIcons: false,
                streetViewControl: false, mapTypeControl: false, fullscreenControl: false,
                gestureHandling: "greedy", zoomControl: true,
              }}>
              <Marker position={center} title={project?.address || ""} />
            </GoogleMap>
          )}
        </div>
        <div className="flex items-center justify-between gap-3 border-t border-slate-100 p-3">
          <p className="text-[11px] text-slate-400 truncate min-w-0">
            {center ? located.locationAddress || `${center.lat.toFixed(5)}, ${center.lng.toFixed(5)}`
              : geoFail ? (zh ? "定位待核对" : "Location needs verification")
              : (zh ? "正在核对门牌位置…" : "Verifying street address…")}
          </p>
          <Button data-testid="hub-address-map-open" size="sm" className="h-8 text-xs shrink-0"
            onClick={() => window.open(url, "_blank", "noopener")}>
            <Navigation className="h-3.5 w-3.5 mr-1" />{zh ? "在 Google 地图中打开" : "Open in Google Maps"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
